import { useState } from "react"
import { Pencil } from "lucide-react"

import { Button, Input } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

export interface EditInputProps {
  className?: string
  value: string
  disabled?: boolean
  onChange?: (value: string) => void
  onSave?: (value: string) => void
  onError?: (error: string) => void
}

const EditInput = ({
  className,
  value,
  disabled = false,
  onChange,
  onSave,
  onError,
}: EditInputProps) => {
  const [currentValue, setCurrentValue] = useState(value)
  const [isEditing, setIsEditing] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.target.value)
    onChange?.(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit()
    }
  }

  const handleSubmit = () => {
    if (currentValue === value) {
      setIsEditing(false)
      onError?.("No changes")
      return
    }

    setIsEditing(false)
    onSave?.(currentValue)
  }

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      {isEditing ? (
        <>
          <Input
            className="flex"
            value={currentValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <div className="flex items-center space-x-2">
            <Button size="sm" onClick={() => handleSubmit()}>
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-center space-x-2">
          <div className={disabled ? "text-slate-500" : ""}>{value}</div>
          {!disabled && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default EditInput
